import { sum, uniq } from 'ramda';
import groupContractCodes from 'constants/groupContractCodes';
import groupProductFamilies from 'constants/groupProductFamilies';
import { isUserEurostarAgent } from 'utils/booking';
import { disabilityTypes, searchTypes, MIN_DB_AGE_WITHOUT_ADULT_OR_SENIOR } from './BookingMagnet.constants';
import memberProductFamilies from '../../constants/memberProductFamilies';
import subscriptionProductFamilies from '../../constants/subscriptionProductFamilies';
import { packageBookingFares } from 'constants/fares';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { COLOGNE_UIC, DUSSELDORF_UIC, DUISBURG_UIC, ESSEN_UIC, DORTMUND_UIC, AACHEN_UIC } from 'constants/directEurostarStations';
import { AMSTERDAM_UIC, ROTTERDAM_UIC } from 'constants/booking';

const NON_DB_GERMAN_STATIONS = [COLOGNE_UIC, DUSSELDORF_UIC, DUISBURG_UIC, ESSEN_UIC, DORTMUND_UIC, AACHEN_UIC];
const NON_NS_DUTCH_STATIONS = [AMSTERDAM_UIC, ROTTERDAM_UIC];

const validateStandardRange = ({ adults12To15, adults16Plus, children5To11, children4Only }) => {
  const totalNumber = sum([adults12To15, adults16Plus, children5To11, children4Only]);
  if (totalNumber === 0 || totalNumber > 9) {
    return 'bookingMagnet.standardBookingRangeError';
  }

  return '';
};

const validateStandardSubscriptionRange = ({ adults, seniors, children, youths }) => {
  const totalNonChildrenNumber = sum([adults, seniors, youths]);
  const totalNumber = sum([adults, seniors, children, youths]);

  if (totalNumber === 0 || totalNumber > 9 || totalNonChildrenNumber > 1) {
    return 'bookingMagnet.standardSubscriptionBookingRangeError';
  }

  return '';
};

const validateGroupRange = ({ adults, children, infants, seatedInfants, youths, seniors }) => {
  const totalNumber = sum([adults, children, youths, seniors, seatedInfants]);

  if (totalNumber === 0 || totalNumber > 99) {
    return 'numberOfPassengersError';
  }

  if (infants > adults) {
    return 'onlyOneInfantError';
  }

  return '';
};

const getRdgStations = (stations, queryFields) => {
  const { origin, destination, returningOrigin, returningDestination } = queryFields;
  const selectedStations = uniq([origin, destination, returningOrigin, returningDestination].filter(Boolean));

  return stations.filter((s) => selectedStations.includes(s.uic) && s.isRdg);
};

const isSNCBStations = (stations, queryFields) => {
  const { origin, destination, returningOrigin, returningDestination } = queryFields;
  const selectedStations = uniq([origin, destination, returningOrigin, returningDestination].filter(Boolean));

  return stations.filter((s) => selectedStations.includes(s.uic) && s.countryCode === 'BEL' && s.uic !== '8814001');
};

const isDBStations = (stations, queryFields) => {
  const { origin, destination, returningOrigin, returningDestination } = queryFields;
  const selectedStations = uniq([origin, destination, returningOrigin, returningDestination].filter(Boolean));

  return stations.filter((s) => selectedStations.includes(s.uic) && s.countryCode === 'DEU' && !NON_DB_GERMAN_STATIONS.includes(s.uic));
};

const isNSStations = (stations, queryFields) => {
  const { origin, destination, returningOrigin, returningDestination } = queryFields;
  const selectedStations = uniq([origin, destination, returningOrigin, returningDestination].filter(Boolean));

  return stations.filter((s) => selectedStations.includes(s.uic) && s.countryCode === 'NLD' && !NON_NS_DUTCH_STATIONS.includes(s.uic));
};

const getDefaultStations = (user, stations) => {
  const trimBase = (str) => str && str.replace(/^STN_/, '');

  const startingOriginCode = stations.find((s) => trimBase(user.defaultJourneyOrigin) === trimBase(s.shortCode));

  const startingDestinationCode = stations.find((s) => trimBase(user.defaultJourneyDestination) === trimBase(s.shortCode));

  return {
    origin: startingOriginCode ? startingOriginCode.uic : '',
    destination: startingDestinationCode ? startingDestinationCode.uic : ''
  };
};

const getFareTypesToDisplay = (
  user,
  selectedSearchType,
  isMember,
  isSubscription,
  disabilityType,
  wheelchairCompanions,
  isPackageBooking
) => {
  const { productFamilies = [], contracts = [], productFamiliesAccessibleTravel = [] } = user;

  const isAccessibleTravel = selectedSearchType === searchTypes.WITH_DISABILITY;

  if (isPackageBooking) {
    return productFamilies
      .filter(({ code }) => packageBookingFares.includes(code))
      ?.sort((a) => {
        if (a.code === productFamilyCodes.RIT) {
          return isAccessibleTravel ? 1 : -1;
        }
        return 1;
      });
  }

  if (isAccessibleTravel) {
    if (
      disabilityType === disabilityTypes.WHEELCHAIR ||
      disabilityType === disabilityTypes.EVAC ||
      (disabilityType === disabilityTypes.COMPANION_ONLY && wheelchairCompanions > 0)
    )
      return productFamiliesAccessibleTravel;
    return productFamilies
      .filter((ft) => !groupProductFamilies.includes(ft.code))
      .sort((a, b) => (a.code === productFamilyCodes.PUB ? -1 : b.code === productFamilyCodes.PUB ? 1 : 0));
  }

  const isGroupSearch = selectedSearchType === searchTypes.GROUP;

  if (isMember && isSubscription && !isGroupSearch) {
    return productFamilies.filter((ft) => subscriptionProductFamilies.includes(ft.code));
  }

  if (isMember && !isGroupSearch) {
    return productFamilies.filter((ft) => memberProductFamilies.includes(ft.code));
  }

  if (isUserEurostarAgent(user)) {
    if (isGroupSearch) {
      return productFamilies.filter((ft) => groupProductFamilies.includes(ft.code));
    }

    return productFamilies
      .filter((ft) => !groupProductFamilies.includes(ft.code))
      .sort((a, b) => (a.code === productFamilyCodes.PUB ? -1 : b.code === productFamilyCodes.PUB ? 1 : 0));
  }

  if (isGroupSearch) {
    return contracts.filter((ft) => groupContractCodes.includes(ft.code));
  }

  return contracts.filter((ft) => !groupContractCodes.includes(ft.code));
};

export const hasAtLeastOnePassenger = (
  {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    adultsWheelchair,
    childrenWheelchair,
    youths,
    seniors,
    guideDogs,
    wheelchairCompanions,
    nonWheelchairCompanions,
    disabilityType,
    adultsEvac,
    childrenEvac
  },
  user
) =>
  (disabilityType !== disabilityTypes.COMPANION_ONLY &&
    disabilityType !== disabilityTypes.ASSISTANCE_DOG_ONLY &&
    sum([
      adults,
      adults12To15,
      adults16Plus,
      children,
      children4Only,
      children5To11,
      adultsWheelchair,
      childrenWheelchair,
      youths,
      seniors,
      adultsEvac,
      childrenEvac
    ]) > 0) ||
  (isUserEurostarAgent(user) &&
    ((disabilityType === disabilityTypes.COMPANION_ONLY && sum([wheelchairCompanions, nonWheelchairCompanions]) > 0) ||
      (disabilityType === disabilityTypes.ASSISTANCE_DOG_ONLY && !!guideDogs)));

const isBookingValid = (formValues, user) => {
  const {
    origin,
    destination,
    outboundDate,
    seniors,
    youths,
    children,
    adults,
    adults12To15,
    adults16Plus,
    bookingTypeSelected,
    containSNCBStation,
    containNSStation
  } = formValues;
  const bookingValid = Boolean(
    origin &&
      origin.length > 0 &&
      destination &&
      destination.length > 0 &&
      outboundDate &&
      outboundDate.isValid() &&
      hasAtLeastOnePassenger(formValues, user) &&
      !hasMoreThan9Passengers(formValues) &&
      (containSNCBStation || containNSStation ? !hasMoreThan4ChildrenPerAdultYouthSeniors(formValues) : true)
  );
  const notChildrenOnly =
    bookingTypeSelected === 'standard'
      ? children === 0 || (children > 0 && sum([seniors, youths, adults, adults12To15, adults16Plus]) > 0)
      : true;
  return bookingValid && notChildrenOnly;
};

const isChildrenOnly = (formValues) => {
  const { seniors, youths, children, adults, adults12To15, adults16Plus, bookingTypeSelected } = formValues;
  return bookingTypeSelected === 'standard' ? children >= 1 && sum([seniors, youths, adults, adults12To15, adults16Plus]) === 0 : false;
};

const hasMoreThan9Passengers = (formValues) => {
  const { seniors, youths, children, adults, adults12To15, adults16Plus, bookingTypeSelected } = formValues;
  return bookingTypeSelected === 'standard' ? sum([seniors, youths, adults, adults12To15, adults16Plus, children]) > 9 : false;
};

const hasMoreThan4ChildrenPerAdultYouthSeniors = (formValues) => {
  const { seniors, youths, children, adults, adults12To15, adults16Plus, bookingTypeSelected } = formValues;
  const nonChildrenNumber = sum([seniors, youths, adults, adults12To15, adults16Plus]);
  return bookingTypeSelected === 'standard' ? children > nonChildrenNumber * 4 : false;
};

const shouldShowRewardFareTypeWarningFor3OrMorePaxs = ({
  adults,
  adults12To15,
  adults16Plus,
  youths,
  seniors,
  children,
  children4Only,
  children5To11,
  infants,
  seatedInfants,
  fareTypeValue,
  infantsOnLapEnabled
}) => {
  const nonChildrenPassengers = [adults, adults12To15, adults16Plus, youths, seniors];

  const childrenPassengers = [children, children4Only, children5To11, infantsOnLapEnabled ? 0 : infants, seatedInfants];

  const isRewardPF = fareTypeValue === productFamilyCodes.REWARD;
  const atLeast3PaxsSelected = sum([...nonChildrenPassengers, ...childrenPassengers]) >= 3;
  const atLeast1PaxSelectedIsNotAChild = sum(nonChildrenPassengers) >= 1;

  return isRewardPF && atLeast3PaxsSelected && atLeast1PaxSelectedIsNotAChild;
};

const areDBPassengersTooYoung = ({
  youthAges = [],
  childAges = [],
  nonWheelchairCompanions = 0,
  passengers = { adults: 0, seniors60To64: 0, seniors65Plus: 0 }
}) => {
  const { adults, seniors60To64, seniors65Plus } = passengers;
  if (adults || seniors60To64 || seniors65Plus || nonWheelchairCompanions) return false;

  return [...youthAges, ...childAges].every(({ value }) => {
    const age = Number(value) || 0;
    return age < MIN_DB_AGE_WITHOUT_ADULT_OR_SENIOR;
  });
};

const shouldAllowFareTypesSelection = ({
  isNewSearchModeEnabled = false,
  bookingTypeSelected = searchTypes.STANDARD,
  containDBStation = false,
  disabilityType = disabilityTypes.WHEELCHAIR
}) => {
  if (isNewSearchModeEnabled) return !(bookingTypeSelected === searchTypes.GROUP || bookingTypeSelected === searchTypes.WITH_DISABILITY);
  return bookingTypeSelected === searchTypes.WITH_DISABILITY && disabilityType === disabilityTypes.WHEELCHAIR ? !containDBStation : true;
};

export {
  isBookingValid,
  getRdgStations,
  validateGroupRange,
  validateStandardRange,
  validateStandardSubscriptionRange,
  getDefaultStations,
  getFareTypesToDisplay,
  isChildrenOnly,
  hasMoreThan9Passengers,
  hasMoreThan4ChildrenPerAdultYouthSeniors,
  shouldShowRewardFareTypeWarningFor3OrMorePaxs,
  isSNCBStations,
  isDBStations,
  areDBPassengersTooYoung,
  shouldAllowFareTypesSelection,
  isNSStations
};
